import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardCheck, FileText, MonitorPlay, Sparkles } from 'lucide-react';
import { GAME_MODE_CATALOG, normalizeGameMode } from '@interactive-room';
import {
  deleteResource,
  duplicateActivityResource,
  duplicateExamResource,
  listResourcesForCourse,
  listResourcesForSubject,
  type StudioResource,
} from '../../studio/studioResources';
import { buildHostLaunchUrl, type HostLaunchIntent } from '../../studio/hostLaunch';
import { buildPrintWorksheetUrl } from '../../studio/print/buildPrintWorksheetUrl';
import { MODE_VISUALS } from '../../theme/designSystem';
import { ResourceActionsMenu, type ResourceMenuItem } from './ResourceActionsMenu';
import { editHref, resourceKey } from '../lib/studioResourceLinks';

interface SubjectResourceLibraryProps {
  subjectId: string;
  subjectHue: string;
  courseId?: string;
  onNewMaterial?: () => void;
  newMaterialLabel?: string;
  /** Modo selector: click selecciona; no navega al editar. */
  selectionMode?: boolean;
  selectedKey?: string | null;
  onSelectResource?: (resource: StudioResource) => void;
}

function ResourceIcon({ resource }: { resource: StudioResource }) {
  if (resource.kind === 'guide') {
    return (
      <span className="studio-resource-row__icon studio-resource-row__icon--exam" aria-hidden>
        <FileText className="h-5 w-5 text-slate-500" strokeWidth={1.75} />
      </span>
    );
  }
  if (resource.kind === 'exam') {
    return (
      <span className="studio-resource-row__icon studio-resource-row__icon--exam" aria-hidden>
        <ClipboardCheck className="h-5 w-5 text-slate-500" strokeWidth={1.75} />
      </span>
    );
  }
  if (resource.kind === 'class') {
    return (
      <span className="studio-resource-row__icon studio-resource-row__icon--class" aria-hidden>
        <MonitorPlay className="h-5 w-5 text-slate-500" strokeWidth={1.75} />
      </span>
    );
  }
  const mode = normalizeGameMode(resource.mode!) ?? resource.mode!;
  const visual = MODE_VISUALS[mode];
  const meta = GAME_MODE_CATALOG[mode];
  return (
    <span
      className="studio-resource-row__icon"
      style={{
        background: visual.tint,
        border: `1px solid color-mix(in srgb, ${visual.hue} 22%, #e2e8f0)`,
      }}
      aria-hidden
    >
      {meta.icon}
    </span>
  );
}

function resourceSubtitle(resource: StudioResource): string {
  if (resource.kind === 'guide') {
    return `${resource.blockCount ?? 0} bloques · Guía`;
  }
  if (resource.kind === 'exam') {
    const parts = [`${resource.blockCount ?? 0} bloques`, 'Evaluación'];
    if (resource.status === 'published' && resource.accessCode) {
      parts.push(`Código ${resource.accessCode}`);
    }
    return parts.join(' · ');
  }
  if (resource.kind === 'class') {
    return `${resource.blockCount ?? 0} diapositivas · Clase`;
  }
  const mode = normalizeGameMode(resource.mode!) ?? resource.mode!;
  return `${GAME_MODE_CATALOG[mode].displayName} · Actividad`;
}

function primaryActionLabel(resource: StudioResource): string {
  if (resource.kind === 'class') return 'Abrir';
  return 'Editar';
}

function buildSecondaryMenuItems(
  resource: StudioResource,
  handlers: {
    onDuplicate: () => void;
    onDelete: () => void;
    onGoLive: () => void;
  },
): ResourceMenuItem[] {
  const items: ResourceMenuItem[] = [
    { id: 'duplicate', label: 'Duplicar', onSelect: handlers.onDuplicate },
    { id: 'live', label: 'Usar en vivo', onSelect: handlers.onGoLive },
  ];

  if (resource.kind === 'guide') {
    items.push({
      id: 'print',
      label: 'Imprimir ficha',
      href: buildPrintWorksheetUrl(resource.id),
      external: true,
    });
  }

  if (resource.kind === 'exam' && resource.status === 'published' && resource.accessCode) {
    items.push({
      id: 'join',
      label: 'Link alumnos',
      href: `/join?mode=exam&code=${resource.accessCode}`,
      external: true,
    });
  }

  items.push({
    id: 'delete',
    label: 'Eliminar',
    onSelect: handlers.onDelete,
    tone: 'danger',
  });

  return items;
}

export function SubjectResourceLibrary({
  subjectId,
  subjectHue,
  courseId,
  onNewMaterial,
  newMaterialLabel = 'Crear material',
  selectionMode = false,
  selectedKey = null,
  onSelectResource,
}: SubjectResourceLibraryProps) {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  const resources = useMemo(
    () => (courseId ? listResourcesForCourse(courseId) : listResourcesForSubject(subjectId)),
    [courseId, subjectId, refreshKey],
  );

  useEffect(() => {
    if (!selectionMode || !onSelectResource || resources.length === 0) return;
    const stillThere = selectedKey && resources.some((r) => resourceKey(r) === selectedKey);
    if (!stillThere) onSelectResource(resources[0]!);
  }, [selectionMode, onSelectResource, resources, selectedKey]);

  const bump = () => setRefreshKey((value) => value + 1);

  const goLive = (resource: StudioResource) => {
    const intent: HostLaunchIntent = {
      kind: resource.kind === 'activity' ? 'activity' : 'exam',
      resourceId: resource.id,
      title: resource.title,
      subjectId,
    };
    navigate(buildHostLaunchUrl(intent));
  };

  const handleDuplicate = (resource: StudioResource) => {
    if (resource.kind === 'activity') duplicateActivityResource(resource.id);
    else duplicateExamResource(resource.id);
    bump();
  };

  const handleDelete = (resource: StudioResource) => {
    const label =
      resource.kind === 'guide' ? 'guía' : resource.kind === 'exam' ? 'evaluación' : 'actividad';
    if (!window.confirm(`¿Eliminar ${label} «${resource.title}»?`)) return;
    deleteResource(resource);
    bump();
  };

  if (resources.length === 0) {
    return (
      <div className="course-materials-empty">
        <p className="course-materials-empty__title">
          {courseId ? 'Aún no hay materiales en este curso' : 'Aún no hay recursos en esta asignatura'}
        </p>
        <p className="course-materials-empty__hint">
          {courseId
            ? 'Genera una guía o ficha con IA usando el contexto del curso.'
            : onNewMaterial
              ? 'Genera un material con IA o elige un caso de uso.'
              : 'Elige un caso de uso en Crear para empezar.'}
        </p>
        {onNewMaterial ? (
          <button type="button" className="course-workspace__cta mt-5" onClick={onNewMaterial}>
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {newMaterialLabel}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <ul
      className={`studio-resource-list ${selectionMode ? 'studio-resource-list--picker space-y-2' : 'space-y-3'}`}
      role={selectionMode ? 'listbox' : undefined}
      aria-label={selectionMode ? 'Materiales del curso' : undefined}
    >
      {resources.map((resource) => {
        const key = resourceKey(resource);
        const selected = selectionMode && selectedKey === key;

        const body = (
          <>
            <ResourceIcon resource={resource} />
            <div className="studio-resource-row__body min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-semibold text-slate-900 sm:text-base">
                  {resource.title}
                </p>
                <span
                  className={`studio-resource-badge studio-resource-badge--${resource.status}`}
                  style={
                    resource.status === 'published'
                      ? { borderColor: `color-mix(in srgb, ${subjectHue} 28%, #e2e8f0)` }
                      : undefined
                  }
                >
                  {resource.status === 'published' ? 'Publicada' : 'Borrador'}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">{resourceSubtitle(resource)}</p>
            </div>
            <div
              className="studio-resource-row__actions"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {!selectionMode ? (
                <Link to={editHref(resource, subjectId, courseId)} className="studio-resource-primary">
                  {primaryActionLabel(resource)}
                </Link>
              ) : null}
              <ResourceActionsMenu
                ariaLabel={`Más acciones para ${resource.title}`}
                items={buildSecondaryMenuItems(resource, {
                  onDuplicate: () => handleDuplicate(resource),
                  onDelete: () => handleDelete(resource),
                  onGoLive: () => goLive(resource),
                })}
              />
            </div>
          </>
        );

        return (
          <li key={key}>
            {selectionMode ? (
              // Nota: no puede ser <button> — ResourceActionsMenu ya renderiza un <button>
              // interno (menú de acciones) y un botón dentro de otro botón es DOM inválido.
              <div
                role="option"
                tabIndex={0}
                aria-selected={selected}
                data-selected={selected ? 'true' : undefined}
                className={`studio-resource-row studio-soft-card studio-resource-row--selectable w-full text-left${
                  selected ? ' studio-resource-row--selected' : ''
                }`}
                onClick={() => onSelectResource?.(resource)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectResource?.(resource);
                  }
                }}
              >
                {body}
              </div>
            ) : (
              <div className="studio-resource-row studio-soft-card">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
